/**
 * UnifiedPreferenceCard - THE SINGLE SOURCE OF TRUTH FOR PREFERENCE CARDS
 * 
 * THIS IS THE LAW - ALL preference cards across the platform MUST use this design:
 * - Host Dashboard (viewing attendees)
 * - Admin People Database (viewing users)
 * - /me page (user's own card)
 * - Share Card pages
 * - Confirmation pages
 * - Apple Wallet preview
 * 
 * CRITICAL RULES:
 * - NO HARDCODED LABELS - Everything from display_settings
 * - Background: event cover_image > host override > category preset color
 * - All badges, sections, theming configurable per category
 * 
 * Last Updated: December 2025
 */

import { useState } from "react";
import { X, Mail, Briefcase, Share, Sparkles, ListChecks, Calendar, Building2, MapPin, Phone, User, Check, Edit3, Eye, Music, ChevronDown, ChevronUp, Link2, Globe, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default category colors - synced with categoryConfig.js (single source of truth)
import { getCategoryPreset } from "@/lib/categoryConfig";
const getCategoryColor = (cat) => {
  const preset = getCategoryPreset(cat);
  if (preset) return { primary: preset.iconColor, bg: preset.iconColor };
  return { primary: "#6366F1", bg: "#4F46E5" };
};

// Label configuration - styled with gradients as per design spec
// Speaker/Panelist: pink-magenta gradient, Host: purple, Pre-Invite: light gray
const DEFAULT_LABELS = [
  { value: "host", label: "Host", color: "bg-purple-600 text-white" },
  { value: "speaker", label: "Speaker/Panelist", color: "bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white" },
  { value: "team", label: "Team", color: "bg-blue-600 text-white" },
  { value: "sponsor", label: "Sponsor/Partner", color: "bg-green-600 text-white" },
  { value: "vip", label: "VIP", color: "bg-amber-500 text-white" },
  { value: "media", label: "Media/Press", color: "bg-cyan-600 text-white" },
  { value: "attendee", label: "Attendee", color: "bg-white/30 text-white backdrop-blur-sm" },
  { value: "pre-invite", label: "Pre-Invite", color: "bg-gray-400/80 text-white" },
  { value: "pre_invite", label: "Pre-Invite", color: "bg-gray-400/80 text-white" },
  { value: "vendor", label: "Vendor", color: "bg-orange-600 text-white" },
  { value: "partner", label: "Partner", color: "bg-teal-600 text-white" }
];

// Get image URL helper
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Unified Preference Card Component
 * 
 * @param {Object} person - The person/RSVP data
 * @param {Object} event - The event data (optional, for context)
 * @param {Object} displaySettings - Dynamic settings for card display
 *   - cardLabel: Custom label (e.g., "ATTENDEE CARD", "GUEST CARD", "MEMBER CARD")
 *   - showSections: Which sections to display
 *   - labelConfig: Custom label colors/names
 * @param {string} category - Category for color theming (hair, makeup, etc.)
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open change handler
 * @param {function} onEdit - Edit button handler (optional)
 * @param {function} onSeeAllDetails - See all details handler (optional)
 * @param {function} onCantMakeIt - Can't make it handler (optional)
 * @param {function} onResendConfirmation - Resend confirmation handler (optional)
 * @param {string} eventId - Event ID for API calls (optional)
 * @param {string} variant - 'modal' | 'inline' | 'compact' (default: 'modal')
 * @param {boolean} showVenueContext - Show "What the venue sees" text
 */
export default function UnifiedPreferenceCard({ 
  person, 
  event, 
  displaySettings = {},
  category = "default",
  open, 
  onOpenChange, 
  onEdit,
  onSeeAllDetails,
  onCantMakeIt,
  onResendConfirmation,
  eventId,
  variant = 'modal',
  showVenueContext = false
}) {
  const [resending, setResending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!person) return null;

  // === DYNAMIC CARD LABEL (NO HARDCODING!) ===
  // Priority: displaySettings.cardLabel > event.display_settings.card_label > category default > "PREFERENCE CARD"
  const cardLabel = displaySettings?.cardLabel || 
                    event?.display_settings?.card_label || 
                    displaySettings?.card_label ||
                    getCategoryDefaultLabel(category);

  // === BACKGROUND LOGIC ===
  // Priority: event.card_background > event.cover_image > host override > category color
  const hasEventBackground = event?.card_background || event?.cover_image || event?.branding?.cover_image;
  const categoryColor = getCategoryColor(category?.toLowerCase());

  // === DYNAMIC LABELS ===
  const personLabels = person.labels || (person.label ? [person.label] : ["attendee"]);
  const labelConfigs = displaySettings?.labelConfig || DEFAULT_LABELS;
  
  const getLabelInfo = (lbl) => {
    const customLabel = labelConfigs.find(l => l.value === lbl);
    const defaultLabel = DEFAULT_LABELS.find(l => l.value === lbl);
    return customLabel || defaultLabel || { label: lbl, color: "bg-white/30 text-white" };
  };

  // === Helper: safely convert any value to array (handles strings) ===
  const toArr = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.trim() ? [val] : [];
    return [];
  };

  // === DIETARY RESTRICTIONS ===
  const dietaryRestrictions = [...new Set([
    ...toArr(person.dietary_restrictions), 
    ...toArr(person.preferences?.dietary_restrictions),
    ...toArr(person.defaults?.dietary?.dietary_restrictions)
  ])];

  // === MEAT PREFERENCES ===
  const meatPreferences = [...new Set([
    ...toArr(person.meat_preferences), 
    ...toArr(person.preferences?.meat_preferences)
  ])];

  // === ALLERGIES ===
  const allergies = [...new Set([
    ...toArr(person.allergies), 
    ...toArr(person.preferences?.allergies)
  ])];

  // === ACCESSIBILITY NEEDS ===
  const accessibilityNeeds = [...new Set([
    ...toArr(person.accessibility_needs), 
    ...toArr(person.preferences?.accessibility_needs),
    ...toArr(person.defaults?.accessibility?.physical_needs)
  ])];

  // === INVISIBLE DISABILITIES (shown as badges if user gave permission) ===
  const invisibleDisabilities = [...new Set([
    ...toArr(person.invisible_disabilities), 
    ...toArr(person.preferences?.invisible_disabilities),
    ...toArr(person.defaults?.accessibility?.invisible_disabilities)
  ])];

  // === INTERESTS ===
  const interests = [...new Set([
    ...toArr(person.interests),
    ...toArr(person.preferences?.interest_preferences),
    ...toArr(person.preferences?.interests)
  ])];

  // === BUILDER PROFILE (Instructor Pilot) ===
  const builderRole = person.preferences?.builder_role || person.builder_role || person.role_type;
  const builderProject = person.preferences?.builder_current_build || person.builder_current_build;
  const builderStage = person.preferences?.builder_stage || person.builder_stage;
  const builderProjectUrl = person.preferences?.builder_project_url || person.builder_project_url;
  const hasBuilderData = builderRole || builderProject || builderStage;

  // === AUDIENCE PREFERENCES ===
  const audienceExp = person.preferences?.audience_experience_level || person.audience_experience_level;
  const audienceStyle = person.preferences?.audience_learning_style || person.audience_learning_style;
  const audiencePace = person.preferences?.audience_pace_preference || person.audience_pace_preference;
  const audienceInteraction = person.preferences?.audience_interaction_pref || person.audience_interaction_pref;
  const audienceGroup = person.preferences?.audience_group_pref || person.audience_group_pref;
  const hasAudienceData = audienceExp || audienceStyle || audiencePace || audienceInteraction || audienceGroup;

  // === QUESTIONS ===
  const questions = person.preferences?.questions || [];
  const singleQuestion = person.preferences?.question_for_instructor || person.question_for_instructor;
  const question1 = person.preferences?.question_1;
  const question2 = person.preferences?.question_2;
  const question3 = person.preferences?.question_3;
  const hasQuestions = questions.length > 0 || singleQuestion || question1 || question2 || question3;

  // === SOCIAL LINKS ===
  const linkedin = person.linkedin || person.preferences?.linkedin;
  const instagram = person.instagram || person.preferences?.instagram;
  const twitter = person.tiktok || person.preferences?.tiktok; // stored as tiktok field for X/Twitter
  const website = person.website || person.preferences?.website;
  const hasSocials = linkedin || instagram || twitter || website;

  // === TAP IN ===
  const tapInLink = person.preferences?.tap_in_link;

  // === VIP STATUS ===
  const hasVip = person.tags?.includes('vip') || person.is_vip;

  // === HANDLE RESEND ===
  const handleResend = async () => {
    if (!eventId || !person.rsvp_id || !person.email) return;
    setResending(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${person.rsvp_id}/resend-confirmation`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Confirmation email sent to ${person.email}`);
      } else {
        toast.error("Failed to resend confirmation");
      }
    } catch (err) {
      toast.error("Failed to resend confirmation");
    } finally {
      setResending(false);
    }
  };

  // Get display name
  const displayName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.name || 'Guest';
  const initials = displayName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "G";

  // === CARD CONTENT ===
  const cardContent = (
    <div className="w-full max-w-sm mx-auto" data-testid="unified-preference-card">
      {/* Card Header - Category Color OR Background Image Override */}
      <div className="relative overflow-hidden rounded-t-2xl">
        {/* Background: Image override OR solid category color */}
        {hasEventBackground ? (
          <div className="absolute inset-0">
            <img 
              src={getImageUrl(event.card_background || event.cover_image || event.branding?.cover_image)} 
              alt={event?.name || 'Event'} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ) : (
          <div 
            className="absolute inset-0" 
            style={{ backgroundColor: categoryColor.bg }}
          />
        )}
        
        <div className="relative px-4 py-4 text-white">
          {/* Header Row - DYNAMIC CARD LABEL */}
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium tracking-wider opacity-80" data-testid="card-label">
              {cardLabel}
            </span>
            {variant === 'modal' && onOpenChange && (
              <button 
                onClick={() => onOpenChange(false)}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                data-testid="close-card-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Event Title with Logo */}
          {event && (
            <div className="flex items-center gap-2 mb-4">
              {(event.logo_image || event.cover_image) && (
                <div className="w-10 h-10 rounded-lg border-2 border-white/50 overflow-hidden shadow-lg bg-black/20 flex-shrink-0">
                  <img 
                    src={getImageUrl(event.logo_image || event.cover_image)} 
                    alt={event.name || 'Event'} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <h3 className="font-semibold text-base drop-shadow-md">{event.name || 'Experience'}</h3>
            </div>
          )}
          
          {/* Profile Section - Centered */}
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-white/20 overflow-hidden border-3 border-white/50 shadow-lg mb-3">
              {(person.profile_photo_url || person.profile_image || person.picture || person.user_photo) ? (
                <img 
                  src={getImageUrl(person.profile_photo_url || person.profile_image || person.picture || person.user_photo)} 
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl font-bold bg-white/10">
                  {initials}
                </div>
              )}
            </div>
            
            <h2 className="text-lg font-bold drop-shadow-md text-center">{displayName}</h2>
            {(person.title || person.company) && (
              <p className="text-xs opacity-80 drop-shadow text-center">
                {person.title}{person.title && person.company ? ' at ' : ''}{person.company}
              </p>
            )}
            
            {/* Dynamic Badges */}
            <div className="flex flex-wrap justify-center gap-1.5 mt-3">
              {personLabels.map((lbl, idx) => {
                const labelInfo = getLabelInfo(lbl);
                return (
                  <Badge 
                    key={idx} 
                    className={`text-[10px] px-2 py-0.5 border-0 ${labelInfo.color}`}
                    data-testid={`label-badge-${lbl}`}
                  >
                    {labelInfo.label}
                  </Badge>
                );
              })}
              {hasVip && !personLabels.includes('vip') && (
                <Badge className="text-[10px] px-2 py-0.5 border-0 bg-amber-500/80 text-white" data-testid="vip-badge">
                  VIP
                </Badge>
              )}
            </div>
            
            {/* Accessibility/Invisible Disabilities as badges (user gave permission to share) */}
            {invisibleDisabilities.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                {invisibleDisabilities.map((disability, idx) => (
                  <Badge 
                    key={idx} 
                    className="text-[10px] px-2 py-0.5 border-0 bg-gradient-to-r from-purple-400 to-purple-600 text-white flex items-center gap-1"
                    data-testid={`disability-badge-${idx}`}
                  >
                    <span className="text-purple-200">💜</span>
                    {disability.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Venue Context Note */}
          {showVenueContext && (
            <p className="text-[10px] text-center opacity-70 mt-3">What the venue sees</p>
          )}
        </div>
      </div>
      
      {/* Preferences Section */}
      <div className="px-6 py-4 bg-white rounded-b-2xl">
        {/* Dietary, Meat Preferences, Allergies & Accessibility */}
        {(dietaryRestrictions.length > 0 || meatPreferences.length > 0 || allergies.length > 0 || accessibilityNeeds.length > 0) && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {dietaryRestrictions.map((d, idx) => (
              <Badge key={`diet-${idx}`} className="bg-orange-400/80 text-white border-0 text-xs">
                {d}
              </Badge>
            ))}
            {meatPreferences.map((m, idx) => (
              <Badge key={`meat-${idx}`} className="bg-green-500/80 text-white border-0 text-xs">
                {m}
              </Badge>
            ))}
            {allergies.map((a, idx) => (
              <Badge key={`allergy-${idx}`} className="bg-red-400/80 text-white border-0 text-xs">
                {a}
              </Badge>
            ))}
            {accessibilityNeeds.map((a, idx) => (
              <Badge key={`acc-${idx}`} className="bg-blue-400/80 text-white border-0 text-xs">
                {a}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Interests */}
        {interests.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1 mb-4">
            {interests.slice(0, 8).map((interest, idx) => (
              <Badge key={idx} variant="outline" className="text-xs text-gray-600">
                {interest}
              </Badge>
            ))}
            {interests.length > 8 && (
              <Badge variant="outline" className="text-xs text-gray-400">
                +{interests.length - 8} more
              </Badge>
            )}
          </div>
        )}
        
        {/* See More Details Toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors mb-2"
          data-testid="see-more-details-toggle"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Show Less" : "See More Details"}
        </button>
        
        {expanded && (
          <div className="space-y-3 mb-4 animate-in slide-in-from-top-2 duration-200">
            {/* Contact Info */}
            {(person.email || person.phone || person.city) && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Contact</p>
                {person.email && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5"><Mail className="w-3 h-3 text-gray-400" />{person.email}</p>
                )}
                {person.phone && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5 mt-0.5"><Phone className="w-3 h-3 text-gray-400" />{person.phone}</p>
                )}
                {person.city && (
                  <p className="text-xs text-gray-600 flex items-center gap-1.5 mt-0.5"><MapPin className="w-3 h-3 text-gray-400" />{person.city}</p>
                )}
              </div>
            )}
            
            {/* Social Links */}
            {hasSocials && (
              <div className="p-2.5 bg-gray-50 rounded-lg">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Social</p>
                <div className="flex flex-wrap gap-2">
                  {linkedin && (
                    <a href={linkedin.startsWith('http') ? linkedin : `https://linkedin.com/in/${linkedin}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1"><Link2 className="w-3 h-3" />LinkedIn</a>
                  )}
                  {instagram && (
                    <span className="text-xs text-pink-600 flex items-center gap-1"><span>@{instagram.replace('@','')}</span></span>
                  )}
                  {twitter && (
                    <span className="text-xs text-gray-700 flex items-center gap-1"><span>@{twitter.replace('@','')}</span></span>
                  )}
                  {website && (
                    <a href={website.startsWith('http') ? website : `https://${website}`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><Globe className="w-3 h-3" />Website</a>
                  )}
                </div>
              </div>
            )}
            
            {/* Builder Profile */}
            {hasBuilderData && (
              <div className="p-2.5 bg-blue-50 rounded-lg">
                <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide mb-1.5">Builder Profile</p>
                {builderRole && <p className="text-xs text-gray-700"><span className="text-gray-500">Role:</span> {builderRole}</p>}
                {builderProject && <p className="text-xs text-gray-700 mt-0.5"><span className="text-gray-500">Building:</span> {builderProject}</p>}
                {builderStage && <p className="text-xs text-gray-700 mt-0.5"><span className="text-gray-500">Stage:</span> {builderStage}</p>}
                {builderProjectUrl && (
                  <a href={builderProjectUrl.startsWith('http') ? builderProjectUrl : `https://${builderProjectUrl}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5"><Link2 className="w-3 h-3" />Project Link</a>
                )}
              </div>
            )}
            
            {/* Audience Preferences */}
            {hasAudienceData && (
              <div className="p-2.5 bg-indigo-50 rounded-lg">
                <p className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wide mb-1.5">Learning Preferences</p>
                <div className="flex flex-wrap gap-1.5">
                  {audienceExp && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">{audienceExp}</Badge>}
                  {audienceStyle && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">{audienceStyle}</Badge>}
                  {audiencePace && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">{audiencePace}</Badge>}
                  {audienceInteraction && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">{audienceInteraction}</Badge>}
                  {audienceGroup && <Badge className="text-[10px] bg-indigo-100 text-indigo-700 border-0">{audienceGroup}</Badge>}
                </div>
              </div>
            )}
            
            {/* Questions */}
            {hasQuestions && (
              <div className="p-2.5 bg-amber-50 rounded-lg">
                <p className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide mb-1.5 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Questions</p>
                <div className="space-y-1.5">
                  {question1 && <p className="text-xs text-gray-700">1. {question1}</p>}
                  {question2 && <p className="text-xs text-gray-700">2. {question2}</p>}
                  {question3 && <p className="text-xs text-gray-700">3. {question3}</p>}
                  {!question1 && !question2 && !question3 && singleQuestion && (
                    <p className="text-xs text-gray-700">{singleQuestion}</p>
                  )}
                  {questions.length > 0 && !question1 && questions.map((q, idx) => (
                    <p key={idx} className="text-xs text-gray-700">{idx+1}. {q.text || q}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tap In */}
            {tapInLink && (
              <div className="p-2.5 bg-green-50 rounded-lg">
                <p className="text-[10px] font-semibold text-green-500 uppercase tracking-wide mb-1">Tap In</p>
                <a href={tapInLink} target="_blank" rel="noopener noreferrer" className="text-xs text-green-700 hover:underline truncate block">{tapInLink}</a>
              </div>
            )}
            
            {/* Dietary Details */}
            {(person.dietary_notes || person.preferences?.dietary_notes) && (
              <div className="p-2.5 bg-orange-50 rounded-lg">
                <p className="text-[10px] font-semibold text-orange-400 uppercase tracking-wide mb-1">Dietary Notes</p>
                <p className="text-xs text-gray-700">{person.dietary_notes || person.preferences?.dietary_notes}</p>
              </div>
            )}
            
            {/* Accessibility Details */}
            {(person.accessibility_notes || person.preferences?.accessibility_notes || person.preferences?.disability_notes) && (
              <div className="p-2.5 bg-purple-50 rounded-lg">
                <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wide mb-1">Accessibility Notes</p>
                <p className="text-xs text-gray-700">{person.accessibility_notes || person.preferences?.accessibility_notes || person.preferences?.disability_notes}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Edit Preferences Button */}
        {onEdit && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mb-2 gap-2"
            onClick={onEdit}
            data-testid="edit-preferences-btn"
          >
            <Edit3 className="w-3 h-3" /> Edit Preferences
          </Button>
        )}
        
        {/* Inspo Images with Source Badges */}
        {((person.preferences?.inspo || person.inspo || []).length > 0) && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center">
              Inspiration
            </p>
            <div className="grid grid-cols-3 gap-2 max-w-[200px] mx-auto">
              {(person.preferences?.inspo || person.inspo || []).slice(0, 6).map((inspo, idx) => {
                const imgUrl = typeof inspo === 'string' ? inspo : inspo.image_url || inspo.url;
                const source = typeof inspo === 'object' ? inspo.source : null;
                return (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative">
                    <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                    {source && (
                      <div className={`absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded text-[8px] font-medium ${
                        source === 'host_curated' ? 'bg-purple-500 text-white' : 'bg-gray-700/80 text-white'
                      }`}>
                        {source === 'host_curated' ? 'Host' : 'You'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Vibe/Music */}
        {(person.preferences?.vibe || person.vibe || person.preferences?.song_request) && (
          <div className="text-center mb-4">
            {(person.preferences?.vibe || person.vibe) && (
              <p className="text-sm text-gray-500 italic">
                "{person.preferences?.vibe || person.vibe}"
              </p>
            )}
            {person.preferences?.song_request && (
              <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-1">
                <Music className="w-3 h-3" />
                <span>{person.preferences.song_request}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          {onSeeAllDetails && (
            <Button
              variant="default"
              size="sm"
              className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
              onClick={onSeeAllDetails}
              data-testid="see-all-details-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              See All Details
            </Button>
          )}
          {onCantMakeIt && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCantMakeIt}
              data-testid="cant-make-it-btn"
            >
              Can't Make It
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  // If modal variant, wrap in Dialog
  if (variant === 'modal') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm w-[95vw] sm:w-auto max-h-[85vh] p-0 overflow-hidden overflow-y-auto border-0 bg-transparent shadow-none" aria-describedby="preference-card-desc">
          <DialogTitle className="sr-only">Preference Card</DialogTitle>
          <DialogDescription id="preference-card-desc" className="sr-only">
            View attendee preferences and information
          </DialogDescription>
          {cardContent}
        </DialogContent>
      </Dialog>
    );
  }

  // For non-modal variants, only render if open is true (when provided)
  // This prevents the card from showing when no preview event is selected
  if (open !== undefined && !open) {
    return null;
  }

  // Inline variant
  return cardContent;
}

/**
 * Get default card label based on category.
 * Iter 9.47.f - labels now category-aware. Each category renders its own
 * preference-card heading. Override per-host via displaySettings.cardLabel.
 * Future: pull from Airtable Categories.Card Label so admins can rename
 * without a redeploy.
 */
function getCategoryDefaultLabel(category) {
  const categoryLabels = {
    hair: "HAIR PREFERENCE CARD",
    makeup: "MAKEUP PREFERENCE CARD",
    nail: "NAIL PREFERENCE CARD",
    nails: "NAIL PREFERENCE CARD",
    lash: "LASH PREFERENCE CARD",
    lashes: "LASH PREFERENCE CARD",
    style: "STYLE PREFERENCE CARD",
    wellness: "WELLNESS PREFERENCE CARD",
    culinary: "CULINARY PREFERENCE CARD",
    instructor: "STUDENT PREFERENCE CARD",
    instructors: "STUDENT PREFERENCE CARD",
    events: "ATTENDEE CARD",
    event: "ATTENDEE CARD",
    travel: "TRAVEL PREFERENCE CARD",
    dispensary: "MEMBER PREFERENCE CARD",
    investor: "INVESTOR CARD",
    default: "PREFERENCE CARD"
  };
  return categoryLabels[category?.toLowerCase()] || categoryLabels.default;
}

/**
 * Compact version for list previews
 */
export function UnifiedPreferenceCardCompact({ 
  person, 
  eventTitle, 
  category = "default",
  displaySettings = {},
  onClick, 
  className = "" 
}) {
  if (!person) return null;
  
  const cardLabel = displaySettings?.cardLabel || 
                    displaySettings?.card_label ||
                    getCategoryDefaultLabel(category);
  const categoryColor = getCategoryColor(category?.toLowerCase());
  const displayName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.name || 'Guest';
  const initials = displayName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "G";

  return (
    <div 
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl cursor-pointer hover:shadow-lg transition-shadow ${className}`}
      data-testid="compact-preference-card"
    >
      <div className="absolute inset-0" style={{ backgroundColor: categoryColor.bg }} />
      <div className="relative p-3 text-white">
        <span className="text-[10px] font-medium tracking-wider opacity-70">{cardLabel}</span>
        <div className="flex items-center gap-2 mt-2">
          <div className="w-8 h-8 rounded-full bg-white/20 overflow-hidden border-2 border-white/30 flex-shrink-0">
            {(person.profile_photo_url || person.profile_image) ? (
              <img 
                src={getImageUrl(person.profile_photo_url || person.profile_image)} 
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs font-bold">
                {initials}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{displayName}</p>
            {eventTitle && <p className="text-[10px] opacity-70 truncate">{eventTitle}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
